import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Plus, Edit3, Trash2, X, Loader2, RefreshCw, Building2, Eye, Upload, ChevronLeft, ChevronRight, Download, Users,
} from 'lucide-react';
import { useDashboard } from '@/context/DashboardContext';
import { useConfirm } from '@/components/dashboard/admin/ConfirmDialog';
import { opportunitiesApi, STATUSES, STATUS_STYLE, imgUrl, money, pct, opTitle, opPlace } from '@/services/opportunitiesApi';

/*
  Editor de Oportunidades (Panel admin → Oportunidades). Cada operación se guarda entera (PUT).
  Las fotos se suben primero (quedan con id propio) y se enlazan al guardar.
  La aportación mínima nunca puede ser inferior a 1.000; la divisa sale del país (Europa → €, resto → $).
*/

const inputCls = 'w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#c9a84c]/40';
const labelCls = 'text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold';
const iconBtn = 'p-1.5 rounded-lg border border-white/10 text-gray-400 hover:text-white disabled:opacity-30';
const FHC_IDS = ['op-2038-sta-maria', 'op-2010-academico', 'op-amaroo'];
const EUROPE = new Set('AD AL AT BA BE BG BY CH CY CZ DE DK EE ES FI FR GB GI GR HR HU IE IS IT LI LT LU LV MC MD ME MK MT NL NO PL PT RO RS SE SI SK SM UA VA XK'.split(' '));

const TEXT = ['code', 'name', 'tagline', 'description', 'city', 'region', 'asset_type', 'surface', 'project_type', 'term', 'units',
  'sale_label', 'scenario', 'return_note', 'vehicle', 'partner', 'market_note', 'cdm_operation_id'];
const NUM = ['total_investment', 'zone_avg_price', 'sale_price_min', 'sale_price_max', 'gross_return_min', 'gross_return_max',
  'annual_yield', 'funding_target', 'funding_raised', 'funding_pct'];

const blank = () => ({
  ...Object.fromEntries(TEXT.map((k) => [k, ''])), ...Object.fromEntries(NUM.map((k) => [k, ''])),
  status: 'Próximamente', country: 'ES', min_contribution: 1000, published: false, order: 0,
  features: [], cost_breakdown: [], comparables: [], market_reference: { label: '', average: '', range_min: '', range_max: '', range_label: '' },
  images: [],
});

function toForm(o) {
  if (!o) return blank();
  const f = blank();
  TEXT.forEach((k) => { f[k] = o[k] ?? ''; });
  NUM.forEach((k) => { f[k] = o[k] ?? ''; });
  Object.assign(f, {
    status: o.status, country: o.country || 'ES', min_contribution: o.min_contribution ?? 1000, published: !!o.published, order: o.order ?? 0,
    features: (o.features || []).map((x) => ({ ...x })), cost_breakdown: (o.cost_breakdown || []).map((x) => ({ ...x })),
    comparables: (o.comparables || []).map((x) => ({ ...x })),
    market_reference: { label: '', average: '', range_min: '', range_max: '', range_label: '', ...(o.market_reference || {}) },
    images: (o.image_ids || []).map((id, i) => ({ id, url: o.images?.[i] })),
  });
  return f;
}

const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

function toPayload(f) {
  const p = {};
  TEXT.forEach((k) => { p[k] = (f[k] || '').trim() || null; });
  NUM.forEach((k) => { p[k] = numOrNull(f[k]); });
  p.name = (f.name || '').trim();
  Object.assign(p, {
    status: f.status, country: (f.country || 'ES').trim().toUpperCase(), min_contribution: Number(f.min_contribution) || 1000,
    published: !!f.published, order: Number(f.order) || 0,
    features: f.features.filter((x) => x.label.trim() && x.value.trim()).map((x) => ({ label: x.label.trim(), value: x.value.trim() })),
    cost_breakdown: f.cost_breakdown.filter((x) => x.label.trim()).map((x) => ({ label: x.label.trim(), amount: Number(x.amount) || 0 })),
    comparables: f.comparables.filter((x) => x.label.trim()).map((x) => ({ label: x.label.trim(), amount: Number(x.amount) || 0 })),
    images: f.images.map((x) => x.id),
  });
  const mr = f.market_reference;
  p.market_reference = (mr.label || mr.average || mr.range_min) ? {
    label: (mr.label || '').trim() || null, average: numOrNull(mr.average), range_min: numOrNull(mr.range_min),
    range_max: numOrNull(mr.range_max), range_label: (mr.range_label || '').trim() || null,
  } : null;
  return p;
}

function Lines({ rows, onChange, labelPh, valueKey = 'amount', valuePh = 'Importe', numeric = true }) {
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2">
          <input value={r.label} onChange={(e) => set(i, 'label', e.target.value)} className={inputCls} placeholder={labelPh} maxLength={120} />
          <input value={r[valueKey]} onChange={(e) => set(i, valueKey, e.target.value)} className={`${inputCls} !w-40`} placeholder={valuePh}
            type={numeric ? 'number' : 'text'} />
          <button type="button" className={iconBtn} onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label="Quitar"><X size={14} /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...rows, { label: '', [valueKey]: '' }])} className="inline-flex items-center gap-1.5 text-xs text-[#c9a84c] hover:underline">
        <Plus size={13} /> Añadir línea
      </button>
    </div>
  );
}

function Field({ label, children, className = '' }) {
  return <div className={className}><label className={labelCls}>{label}</label>{children}</div>;
}

function Editor({ opp, onClose, onSaved }) {
  const [f, setF] = useState(() => toForm(opp));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const setMr = (k) => (e) => setF((x) => ({ ...x, market_reference: { ...x.market_reference, [k]: e.target.value } }));
  const cur = EUROPE.has((f.country || '').toUpperCase()) ? 'EUR' : 'USD';
  const costSum = f.cost_breakdown.reduce((a, c) => a + (Number(c.amount) || 0), 0);

  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const r = await opportunitiesApi.adminUploadImage(file);
        setF((x) => ({ ...x, images: [...x.images, { id: r.id, url: r.url }] }));
      }
    } catch (err) { toast.error(err.message); } finally { setUploading(false); }
  };
  const moveImg = (i, d) => setF((x) => {
    const a = [...x.images]; const j = i + d;
    if (j < 0 || j >= a.length) return x;
    [a[i], a[j]] = [a[j], a[i]];
    return { ...x, images: a };
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error('La operación necesita un nombre');
    if (Number(f.min_contribution) < 1000) return toast.error('La aportación mínima no puede ser inferior a 1.000');
    setSaving(true);
    try {
      const p = toPayload(f);
      if (opp?.id) await opportunitiesApi.adminUpdate(opp.id, p); else await opportunitiesApi.adminCreate(p);
      toast.success('Oportunidad guardada');
      onSaved();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center overflow-y-auto bg-black/60 p-2 sm:p-4" role="dialog" aria-modal="true">
      <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="my-auto w-full max-w-4xl rounded-2xl border border-white/10 bg-[#0d1829] shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-white/10 p-4 sm:p-5">
          <h3 className="text-white font-semibold truncate">{opp?.id ? `Editar: ${opTitle(opp)}` : 'Nueva oportunidad'}</h3>
          <button type="button" onClick={onClose} aria-label="Cerrar"><X size={18} className="text-gray-400 hover:text-white" /></button>
        </div>
        <form onSubmit={submit} className="max-h-[78vh] space-y-6 overflow-y-auto p-4 sm:p-5" data-testid="opportunity-editor">
          <section className="grid gap-3 sm:grid-cols-4">
            <Field label="Código"><input value={f.code} onChange={set('code')} className={inputCls} placeholder="2038" maxLength={20} /></Field>
            <Field label="Nombre *" className="sm:col-span-2"><input value={f.name} onChange={set('name')} className={inputCls} placeholder="Sta. María" maxLength={120} /></Field>
            <Field label="Estado">
              <select value={f.status} onChange={set('status')} className={inputCls}>{STATUSES.map((s) => <option key={s} className="bg-[#0d1829]">{s}</option>)}</select>
            </Field>
            <Field label="Lema" className="sm:col-span-2"><input value={f.tagline} onChange={set('tagline')} className={inputCls} maxLength={160} /></Field>
            <Field label="Ciudad"><input value={f.city} onChange={set('city')} className={inputCls} maxLength={80} /></Field>
            <Field label="Provincia / región"><input value={f.region} onChange={set('region')} className={inputCls} maxLength={80} /></Field>
            <Field label={`País (código) · divisa ${cur === 'EUR' ? '€' : '$'}`}><input value={f.country} onChange={set('country')} className={inputCls} maxLength={2} /></Field>
            <Field label="Orden"><input type="number" min="0" value={f.order} onChange={set('order')} className={inputCls} /></Field>
            <label className="sm:col-span-2 flex items-center gap-2 pt-5 text-sm text-gray-300">
              <input type="checkbox" checked={f.published} onChange={set('published')} className="accent-[#c9a84c]" /> Publicada (la ven los socios)
            </label>
            <Field label="Descripción" className="sm:col-span-4"><textarea rows={3} value={f.description} onChange={set('description')} className={`${inputCls} resize-y`} maxLength={4000} /></Field>
          </section>

          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-white">Fotos</h4>
            <div className="flex flex-wrap gap-3">
              {f.images.map((im, i) => (
                <div key={im.id} className="relative w-36">
                  <img src={imgUrl(im.url || `/api/public/opportunities/images/${im.id}`)} alt="" className="aspect-[4/3] w-36 rounded-lg border border-white/10 object-cover" />
                  {i === 0 && <span className="absolute left-1.5 top-1.5 rounded bg-[#c9a84c] px-1.5 text-[10px] font-semibold text-[#0a1628]">Portada</span>}
                  <div className="mt-1 flex justify-between">
                    <button type="button" className={iconBtn} onClick={() => moveImg(i, -1)} disabled={i === 0} aria-label="Mover a la izquierda"><ChevronLeft size={13} /></button>
                    <button type="button" className={`${iconBtn} hover:!text-red-300`} onClick={() => setF((x) => ({ ...x, images: x.images.filter((_, j) => j !== i) }))} aria-label="Quitar foto"><Trash2 size={13} /></button>
                    <button type="button" className={iconBtn} onClick={() => moveImg(i, 1)} disabled={i === f.images.length - 1} aria-label="Mover a la derecha"><ChevronRight size={13} /></button>
                  </div>
                </div>
              ))}
              <label className="flex aspect-[4/3] w-36 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-white/15 text-xs text-gray-400 hover:border-[#c9a84c]/40 hover:text-white">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Subir fotos
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={upload} disabled={uploading} />
              </label>
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-white">Ficha</h4>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Tipo de activo"><input value={f.asset_type} onChange={set('asset_type')} className={inputCls} /></Field>
              <Field label="Superficie"><input value={f.surface} onChange={set('surface')} className={inputCls} placeholder="64 m² construidos" /></Field>
              <Field label="Estado del proyecto"><input value={f.project_type} onChange={set('project_type')} className={inputCls} placeholder="Comprar, reformar y vender" /></Field>
              <Field label="Plazo estimado"><input value={f.term} onChange={set('term')} className={inputCls} placeholder="9 meses" /></Field>
              <Field label="Unidades" className="sm:col-span-2"><input value={f.units} onChange={set('units')} className={inputCls} placeholder="36 viviendas · 56 garajes" /></Field>
            </div>
            <Field label="Otros datos (dormitorios, parcela…)">
              <Lines rows={f.features} onChange={(v) => setF((x) => ({ ...x, features: v }))} labelPh="Dato" valueKey="value" valuePh="Valor" numeric={false} />
            </Field>
          </section>

          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-white">Cifras ({cur === 'EUR' ? '€' : '$'})</h4>
            <div className="grid gap-3 sm:grid-cols-4">
              <Field label="Inversión total estimada"><input type="number" value={f.total_investment} onChange={set('total_investment')} className={inputCls} /></Field>
              <Field label="Precio medio de la zona"><input type="number" value={f.zone_avg_price} onChange={set('zone_avg_price')} className={inputCls} /></Field>
              <Field label="Venta estimada (mín. o única)"><input type="number" value={f.sale_price_min} onChange={set('sale_price_min')} className={inputCls} /></Field>
              <Field label="Venta estimada (máx.)"><input type="number" value={f.sale_price_max} onChange={set('sale_price_max')} className={inputCls} /></Field>
              <Field label="Texto de la venta" className="sm:col-span-2"><input value={f.sale_label} onChange={set('sale_label')} className={inputCls} placeholder="Rango de venta estimado / Precio estimado de venta" /></Field>
              <Field label="Rentab. bruta total mín. (%)"><input type="number" value={f.gross_return_min} onChange={set('gross_return_min')} className={inputCls} /></Field>
              <Field label="Rentab. bruta total máx. (%)"><input type="number" value={f.gross_return_max} onChange={set('gross_return_max')} className={inputCls} /></Field>
              <Field label="Rendimiento explotación (% anual)"><input type="number" value={f.annual_yield} onChange={set('annual_yield')} className={inputCls} /></Field>
              <Field label="Escenario"><input value={f.scenario} onChange={set('scenario')} className={inputCls} placeholder="Conservador" /></Field>
              <Field label="Nota de la rentabilidad" className="sm:col-span-2"><input value={f.return_note} onChange={set('return_note')} className={inputCls} maxLength={300} /></Field>
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-white">Inversión necesaria y estructura de costes {costSum > 0 && <span className="text-xs font-normal text-gray-400">· total {money(costSum, cur)}</span>}</h4>
            <Lines rows={f.cost_breakdown} onChange={(v) => setF((x) => ({ ...x, cost_breakdown: v }))} labelPh="Concepto (Compra del activo, Reforma…)" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Vehículo"><input value={f.vehicle} onChange={set('vehicle')} className={inputCls} maxLength={300} /></Field>
              <Field label="Partner operador"><input value={f.partner} onChange={set('partner')} className={inputCls} maxLength={120} placeholder="BUVE" /></Field>
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-white">Estudio de mercado</h4>
            <Field label="Explicación"><textarea rows={2} value={f.market_note} onChange={set('market_note')} className={`${inputCls} resize-y`} maxLength={1000} /></Field>
            <div className="grid gap-3 sm:grid-cols-5">
              <Field label="Activo de referencia" className="sm:col-span-2"><input value={f.market_reference.label} onChange={setMr('label')} className={inputCls} /></Field>
              <Field label="P.V.P. medio"><input type="number" value={f.market_reference.average} onChange={setMr('average')} className={inputCls} /></Field>
              <Field label="Rango desde"><input type="number" value={f.market_reference.range_min} onChange={setMr('range_min')} className={inputCls} /></Field>
              <Field label="Rango hasta"><input type="number" value={f.market_reference.range_max} onChange={setMr('range_max')} className={inputCls} /></Field>
              <Field label="Nombre del rango" className="sm:col-span-2"><input value={f.market_reference.range_label} onChange={setMr('range_label')} className={inputCls} placeholder="Rango Casafari" /></Field>
            </div>
            <Field label="Comparables">
              <Lines rows={f.comparables} onChange={(v) => setF((x) => ({ ...x, comparables: v }))} labelPh="Comparable" />
            </Field>
          </section>

          <section className="space-y-3">
            <h4 className="text-sm font-semibold text-white">Aportaciones</h4>
            <div className="grid gap-3 sm:grid-cols-4">
              <Field label="Aportación mínima (≥ 1.000)"><input type="number" min="1000" value={f.min_contribution} onChange={set('min_contribution')} className={inputCls} data-testid="opp-min" /></Field>
              <Field label="Objetivo"><input type="number" value={f.funding_target} onChange={set('funding_target')} className={inputCls} placeholder="= inversión total" /></Field>
              <Field label="Aportado"><input type="number" value={f.funding_raised} onChange={set('funding_raised')} className={inputCls} /></Field>
              <Field label="% manual (opcional)"><input type="number" min="0" max="100" value={f.funding_pct} onChange={set('funding_pct')} className={inputCls} /></Field>
              <Field label="Operación del cuadro de mando (OP1…)"><input value={f.cdm_operation_id} onChange={(e) => setF((x) => ({ ...x, cdm_operation_id: e.target.value.toUpperCase().replace(/\s/g, '') }))} className={inputCls} maxLength={10} placeholder="OP1" data-testid="opp-cdm-op" /></Field>
            </div>
            <p className="text-xs text-gray-400">Con la operación del cuadro de mando, cada socio que haya aportado ve esta ficha desde «Mis operaciones».</p>
            <p className="text-xs text-gray-500">La barra usa el % manual si lo rellenas; si no, aportado ÷ objetivo (o la inversión total).</p>
          </section>

          <div className="sticky bottom-0 -mx-4 -mb-4 flex justify-end gap-2 border-t border-white/10 bg-[#0d1829] px-4 py-3 sm:-mx-5 sm:-mb-5 sm:px-5">
            <button type="button" onClick={onClose} className="rounded-lg border border-white/10 px-4 py-2 text-sm text-gray-300 hover:text-white">Cancelar</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628] disabled:opacity-50" data-testid="opportunity-save">
              {saving && <Loader2 size={14} className="animate-spin" />} Guardar
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

const INTEREST_STATUSES = ['Nueva', 'Contactado', 'Convertida', 'Descartada'];

function Interests({ opp, onClose }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => opportunitiesApi.adminInterests(opp.id).then(setRows).catch((e) => { toast.error(e.message); setRows([]); }), [opp.id]);
  useEffect(() => { load(); }, [load]);
  const change = async (r, status) => {
    try { await opportunitiesApi.adminInterestStatus(r.id, status); load(); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-3xl rounded-2xl border border-white/10 bg-[#0d1829] p-5 shadow-2xl space-y-4" data-testid="opportunity-interests">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold">Solicitudes · {opTitle(opp)}</h3>
          <button type="button" onClick={onClose} aria-label="Cerrar"><X size={18} className="text-gray-400 hover:text-white" /></button>
        </div>
        {!rows ? <Loader2 className="animate-spin text-[#c9a84c]" size={18} /> : !rows.length ? <p className="text-sm text-gray-400">Todavía no hay solicitudes.</p> : (
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full min-w-[620px] text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="py-2 pr-3">Socio</th><th className="py-2 pr-3">Contacto</th><th className="py-2 pr-3 text-right">Importe</th><th className="py-2 pr-3">Fecha</th><th className="py-2">Estado</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-white/[0.05] align-top text-gray-300">
                    <td className="py-2 pr-3 text-white">{r.name || '—'}{r.user_deleted_at && <span className="ml-2 rounded-full border border-red-400/20 bg-red-400/10 px-2 py-0.5 text-[11px] text-red-300">Usuario eliminado</span>}{r.message && <p className="text-xs text-gray-400 mt-0.5">«{r.message}»</p>}</td>
                    <td className="py-2 pr-3 text-xs">{r.email}<br />{r.phone}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-[#e3c87a]">{money(r.amount, r.currency)}</td>
                    <td className="py-2 pr-3 text-xs whitespace-nowrap">{(r.updated_at || r.created_at || '').slice(0, 10)}</td>
                    <td className="py-2">
                      <select value={r.status} onChange={(e) => change(r, e.target.value)} className={`${inputCls} !w-auto !py-1`}>
                        {INTEREST_STATUSES.map((s) => <option key={s} className="bg-[#0d1829]">{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function OpportunityManager() {
  const [dialog, ask] = useConfirm();
  const { user } = useDashboard();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const load = useCallback(() => opportunitiesApi.adminList().then(setItems).catch((e) => { toast.error(e.message); setItems([]); }), []);
  useEffect(() => { load(); }, [load]);

  const remove = async (o) => {
    const ok = await ask({
      title: `Eliminar «${opTitle(o)}»`,
      details: [
        'Deja de verse en el panel de los miembros.',
        'Se borran también sus fotos.',
        `Sus solicitudes (${o.interest_count || 0}) se conservan.`,
      ],
      confirmLabel: 'Eliminar oportunidad',
    });
    if (!ok) return;
    try { await opportunitiesApi.adminDelete(o.id); toast.success('Oportunidad eliminada'); load(); } catch (e) { toast.error(e.message); }
  };
  const importFhc = async () => {
    const ok = await ask({
      title: 'Cargar las operaciones del club',
      details: [
        'Crea las operaciones reales del club que falten (Amaroo, Tortosa y León) con sus datos y fotos.',
        'No toca las que ya existen ni crea datos de prueba.',
      ],
      confirmLabel: 'Cargar operaciones', tone: 'gold',
    });
    if (!ok) return;
    try { const r = await opportunitiesApi.adminImport(); toast.success(r.created ? `${r.created} operaciones cargadas` : 'Ya estaban cargadas'); load(); } catch (e) { toast.error(e.message); }
  };

  if (!items) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#c9a84c]" size={24} /></div>;
  const missingFhc = FHC_IDS.some((id) => !items.some((o) => o.id === id));

  return (
    <div className="space-y-4" data-testid="opportunity-manager">
      {dialog}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-400">{items.length} {items.length === 1 ? 'operación' : 'operaciones'} · las no publicadas solo las ve el equipo</p>
        <div className="flex gap-2">
          <button onClick={load} className="rounded-lg border border-white/10 p-2 text-gray-400 hover:text-white" aria-label="Recargar"><RefreshCw size={15} /></button>
          {isAdmin && missingFhc && (
            <button onClick={importFhc} className="inline-flex items-center gap-2 rounded-lg border border-[#c9a84c]/30 px-3 py-2 text-sm text-[#e3c87a] hover:bg-[#c9a84c]/10"><Download size={15} /> Cargar operaciones del club</button>
          )}
          <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 rounded-lg bg-[#c9a84c] px-3 py-2 text-sm font-semibold text-[#0a1628]" data-testid="opportunity-new"><Plus size={15} /> Nueva oportunidad</button>
        </div>
      </div>
      {items.map((o) => (
        <div key={o.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="flex aspect-video w-28 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#c9a84c]/10">
            {o.images?.[0] ? <img src={imgUrl(o.images[0])} alt="" className="h-full w-full object-cover" /> : <Building2 size={18} className="text-[#c9a84c]" />}
          </div>
          <div className="min-w-[200px] flex-1">
            <p className="text-white font-medium">{opTitle(o)}</p>
            <p className="text-xs text-gray-400 mt-0.5">{[opPlace(o), `mín. ${money(o.min_contribution, o.currency)}`, `aportaciones ${pct(o.progress?.pct)}`].join(' · ')}</p>
          </div>
          <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[o.status] || ''}`}>{o.status}</span>
          {!o.published && <span className="rounded-full border border-amber-400/20 bg-amber-400/10 px-2.5 py-0.5 text-[11px] font-semibold text-amber-200">No publicada</span>}
          <div className="flex gap-2">
            <button onClick={() => setViewing(o)} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1.5 text-xs text-white hover:bg-white/[0.05]"><Users size={13} /> {o.interest_count}</button>
            <a href={`/dashboard/propiedades/${encodeURIComponent(o.id)}`} target="_blank" rel="noopener noreferrer" className="rounded-lg border border-white/10 p-2 text-gray-400 hover:text-white" aria-label={`Ver ${opTitle(o)}`}><Eye size={14} /></a>
            <button onClick={() => setEditing(o)} className="rounded-lg border border-white/10 p-2 text-gray-400 hover:text-white" aria-label={`Editar ${opTitle(o)}`}><Edit3 size={14} /></button>
            {isAdmin && <button onClick={() => remove(o)} className="rounded-lg border border-white/10 p-2 text-gray-400 hover:text-red-300" aria-label={`Eliminar ${opTitle(o)}`}><Trash2 size={14} /></button>}
          </div>
        </div>
      ))}
      {editing && <Editor opp={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {viewing && <Interests opp={viewing} onClose={() => { setViewing(null); load(); }} />}
    </div>
  );
}
